import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MdEventBusy, MdMeetingRoom, MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { getRooms, Room } from '@/lib/api/rooms/rooms-api'
import PaginationControls from '@/components/pagination/PaginationControls'
import OptionDropdown from './option-dropdown'
import {
    APPOINTMENT_KIND_LABELS,
    AppointmentKind,
    createPresenceEntry,
    PresenceStatus,
    ScheduledAppointment,
    searchPresenceAppointments,
} from '@/lib/api/presence/presence-api'

interface PresenceAssignProps {
    statuses: PresenceStatus[]
    onAssigned: () => void
}

const KIND_FILTERS: AppointmentKind[] = ['CONSULTATION', 'EXAM', 'ORDER']

/** Agendamentos por página. */
const PAGE_LIMIT = 20

type PeriodKey = 'ALL' | 'TODAY' | 'WEEK' | 'MONTH' | 'YEAR'

const PERIOD_FILTERS: Array<{ key: PeriodKey; label: string }> = [
    { key: 'ALL', label: 'Todo período' },
    { key: 'TODAY', label: 'Hoje' },
    { key: 'WEEK', label: 'Esta semana' },
    { key: 'MONTH', label: 'Este mês' },
    { key: 'YEAR', label: 'Este ano' },
]

/**
 * Janela do período escolhido, em ISO local.
 *
 * "Todo período" não devolve nada: sem janela, o backend não filtra data
 * nenhuma. É o padrão porque o paciente que chega ao balcão pode ter sido
 * agendado para qualquer dia, e um recorte silencioso já custou buscas que não
 * achavam ninguém.
 */
function periodRange(period: PeriodKey): {
    startDate?: string
    endDate?: string
} {
    if (period === 'ALL') return {}

    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const end = new Date()
    end.setHours(23, 59, 59, 999)

    if (period === 'WEEK') {
        // Semana começando no domingo, como o calendário do sistema.
        start.setDate(start.getDate() - start.getDay())
        // O fim se calcula A PARTIR do início já deslocado, e não somando dias
        // ao `end` de hoje: numa semana que atravessa o mês, `start.getDate()`
        // vale 30 e "30 + 6" jogaria o fim para outubro — a janela cobria cinco
        // semanas em vez de uma.
        end.setTime(start.getTime())
        end.setDate(end.getDate() + 6)
        end.setHours(23, 59, 59, 999)
    }
    if (period === 'MONTH') {
        start.setDate(1)
        end.setMonth(end.getMonth() + 1, 0)
    }
    if (period === 'YEAR') {
        start.setMonth(0, 1)
        end.setMonth(11, 31)
    }

    const iso = (date: Date) => {
        const local = new Date(date)
        local.setMinutes(local.getMinutes() - local.getTimezoneOffset())
        return local.toISOString().substring(0, 19)
    }
    return { startDate: iso(start), endDate: iso(end) }
}

/** O cadastro guarda CPF e CNPJ no mesmo campo; o rótulo segue o tamanho. */
function documentLabel(document: string): string {
    return document.replace(/\D/g, '').length > 11 ? 'CNPJ' : 'CPF'
}

function formatSchedule(value: string | null, arrivalOrder: boolean): string {
    if (!value) return 'Sem data'
    const date = new Date(value)
    const day = date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
    })
    if (arrivalOrder) return `${day} · ordem de chegada`
    return `${day} ${date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
    })}`
}

/**
 * Aba "Definir controle": acha o agendamento de quem chegou e pendura o controle
 * escolhido no paciente.
 *
 * Só entram agendamentos com status AGENDADO: quem se apresenta no balcão vem
 * para um horário marcado. Orçamento, solicitação sem agenda, atendimento já em
 * andamento, concluído e cancelado ficam de fora.
 *
 * A lista abre em "Todo período" e já vem preenchida, antes de qualquer busca —
 * no balcão, quem chega costuma ser o próximo da lista, e obrigar a digitar o
 * nome seria trabalho a mais.
 */
export default function PresenceAssign({
    statuses,
    onAssigned,
}: PresenceAssignProps) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounce(search, 400)
    const [kinds, setKinds] = useState<AppointmentKind[]>(KIND_FILTERS)
    const [period, setPeriod] = useState<PeriodKey>('ALL')
    const [appointments, setAppointments] = useState<ScheduledAppointment[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    // Guarda de corrida: "Definir" recarrega a lista ao mesmo tempo que o efeito
    // dos filtros, e a resposta que chegasse por último vencia — a pessoa que
    // acabou de entrar no painel reaparecia na lista, com o total desatualizado.
    // Só a resposta do pedido mais recente é aplicada.
    const requestRef = useRef(0)
    const [assigningKey, setAssigningKey] = useState<string | null>(null)
    // Sala escolhida linha a linha, antes de definir. A do topo é só o padrão:
    // quem encaminha o paciente para uma sala diferente da anterior não deveria
    // ter que voltar ao cabeçalho e desfazer depois.
    const [roomByAppointment, setRoomByAppointment] = useState<
        Record<string, number | ''>
    >({})

    const activeStatuses = useMemo(
        () => statuses.filter(status => status.active),
        [statuses]
    )
    const [statusId, setStatusId] = useState<number | null>(null)
    // Sala é opcional: muita recepção só encaminha depois, já com o paciente
    // esperando. Quando escolhida aqui, entra junto com a chegada no histórico.
    const [roomId, setRoomId] = useState<number | ''>('')
    const [rooms, setRooms] = useState<Room[]>([])

    useEffect(() => {
        getRooms().then(response => {
            if (response.success && response.result) {
                setRooms(response.result.filter(room => room.active))
            }
        })
    }, [])

    useEffect(() => {
        if (statusId === null && activeStatuses.length > 0) {
            // Começa no controle de maior posição numérica (o "menos urgente"),
            // que é o caso comum de quem chega para um horário marcado.
            setStatusId(activeStatuses[activeStatuses.length - 1].id)
        }
    }, [activeStatuses, statusId])

    const loadAppointments = useCallback(async () => {
        const requestId = ++requestRef.current
        setLoading(true)
        const term = debouncedSearch.trim()
        const response = await searchPresenceAppointments({
            search: term || undefined,
            kinds:
                kinds.length === KIND_FILTERS.length
                    ? undefined
                    : kinds.join(','),
            ...periodRange(period),
            limit: PAGE_LIMIT,
            page,
        })
        if (requestId !== requestRef.current) return
        setLoading(false)
        if (!response.success) {
            toast.error(
                response.error || 'Não foi possível carregar os agendamentos'
            )
            return
        }
        setAppointments(response.result?.items ?? [])
        setTotal(response.result?.total ?? 0)
    }, [debouncedSearch, kinds, period, page])

    // Mudou o que se está procurando, a página volta ao começo: manter a
    // página 5 depois de trocar o filtro mostraria uma lista vazia sem motivo
    // aparente.
    useEffect(() => {
        setPage(1)
    }, [debouncedSearch, kinds, period])

    useEffect(() => {
        loadAppointments()
    }, [loadAppointments])

    const toggleKind = (kind: AppointmentKind) => {
        setKinds(current => {
            // Desmarcar o último filtro deixaria a busca sem tipo nenhum: nesse
            // caso volta a valer "todos", que é o estado inicial.
            if (current.includes(kind)) {
                const next = current.filter(item => item !== kind)
                return next.length === 0 ? KIND_FILTERS : next
            }
            return [...current, kind]
        })
    }

    const handleAssign = async (
        appointment: ScheduledAppointment,
        chosenStatusId?: number
    ) => {
        // Sem escolha na linha, vale o controle do topo — o padrão da fila.
        const targetStatusId = chosenStatusId ?? statusId
        if (!targetStatusId) {
            toast.error('Escolha o controle antes de definir a presença')
            return
        }
        const key = `${appointment.kind}:${appointment.id}`
        const targetRoomId = roomByAppointment[key] ?? roomId
        setAssigningKey(key)
        const response = await createPresenceEntry({
            statusId: targetStatusId,
            kind: appointment.kind,
            appointmentId: appointment.id,
            roomId: targetRoomId === '' ? undefined : targetRoomId,
        })
        setAssigningKey(null)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível definir a presença')
            return
        }
        toast.success(`${appointment.client.name} está no controle de presença`)
        // A sala volta a "sem sala": ela é específica de cada paciente, e herdar
        // a do anterior encaminharia gente para a sala errada sem ninguém notar.
        // O controle, esse, fica — quem chega costuma entrar no mesmo status.
        setRoomId('')
        setRoomByAppointment(current => {
            const next = { ...current }
            delete next[key]
            return next
        })
        await loadAppointments()
        onAssigned()
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                    <label
                        className="text-sm font-medium"
                        htmlFor="presence-search"
                    >
                        Buscar paciente
                    </label>
                    <label className="input input-bordered flex items-center gap-2 !rounded-lg">
                        <MdSearch className="text-lg text-base-content/50" />
                        <input
                            id="presence-search"
                            type="text"
                            className="grow"
                            placeholder="Nome, CPF ou título do agendamento"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                        />
                    </label>
                </div>

                <div className="flex flex-wrap gap-2">
                    {PERIOD_FILTERS.map(option => (
                        <button
                            key={option.key}
                            type="button"
                            className={`btn btn-sm !rounded-full ${
                                period === option.key
                                    ? 'btn-neutral'
                                    : 'btn-ghost border border-base-300'
                            }`}
                            onClick={() => setPeriod(option.key)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                <div className="flex flex-wrap gap-2">
                    {KIND_FILTERS.map(kind => {
                        const selected = kinds.includes(kind)
                        return (
                            <button
                                key={kind}
                                type="button"
                                className={`btn btn-sm !rounded-full ${
                                    selected ? 'btn-primary' : 'btn-outline'
                                }`}
                                onClick={() => toggleKind(kind)}
                            >
                                {APPOINTMENT_KIND_LABELS[kind]}
                            </button>
                        )
                    })}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">
                            Controle a aplicar
                        </span>
                        <OptionDropdown
                            ariaLabel="Controle a aplicar"
                            className="h-10 w-full"
                            value={statusId ?? ''}
                            options={activeStatuses.map(status => ({
                                value: status.id,
                                label: `${status.position}. ${status.name}`,
                                color: status.color,
                            }))}
                            onChange={value => setStatusId(Number(value))}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium">
                            Sala / consultório{' '}
                            <span className="font-normal text-base-content/50">
                                (opcional)
                            </span>
                        </span>
                        <OptionDropdown
                            ariaLabel="Sala / consultório"
                            className="h-10 w-full"
                            placeholder="Sem sala definida"
                            value={roomId}
                            options={[
                                { value: '', label: 'Sem sala definida' },
                                ...rooms.map(room => ({
                                    value: room.id,
                                    label: room.name,
                                    color: room.color,
                                    hint: room.description,
                                })),
                            ]}
                            onChange={value => setRoomId(value)}
                        />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10">
                    <span className="loading loading-spinner loading-md" />
                </div>
            ) : appointments.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                    <MdEventBusy className="text-4xl text-base-content/30" />
                    <p className="text-sm font-medium">
                        Nenhum agendamento encontrado
                    </p>
                    <p className="text-xs text-base-content/60">
                        {period === 'ALL'
                            ? 'Somente agendamentos com status agendado, de qualquer data, começando pelos mais próximos de hoje.'
                            : 'Somente agendamentos com status agendado dentro do período escolhido.'}
                    </p>
                </div>
            ) : (
                <>
                    {period === 'ALL' && (
                        <p className="text-xs text-base-content/60">
                            Ordenados pelos agendamentos mais próximos de hoje.
                        </p>
                    )}

                    <ul className="flex flex-col gap-2">
                        {appointments.map(appointment => {
                            const key = `${appointment.kind}:${appointment.id}`
                            return (
                                <li
                                    key={key}
                                    className="flex flex-col gap-2 rounded-xl border border-base-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="truncate font-semibold">
                                                {appointment.client.name}
                                            </span>
                                            <span className="badge badge-ghost badge-sm">
                                                {
                                                    APPOINTMENT_KIND_LABELS[
                                                        appointment.kind
                                                    ]
                                                }
                                            </span>
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-base-content/70">
                                            <span className="truncate">
                                                {appointment.title}
                                            </span>
                                            {appointment.professional && (
                                                <span className="truncate">
                                                    {
                                                        appointment.professional
                                                            .name
                                                    }
                                                </span>
                                            )}
                                            <span>
                                                {formatSchedule(
                                                    appointment.scheduledAt,
                                                    appointment.arrivalOrder
                                                )}
                                            </span>
                                            {(roomByAppointment[key] ?? roomId) !==
                                            '' && (
                                            <span className="flex items-center gap-1 text-primary">
                                                <MdMeetingRoom className="text-sm" />
                                                {
                                                    rooms.find(
                                                        room =>
                                                            room.id ===
                                                            (roomByAppointment[
                                                                key
                                                            ] ?? roomId)
                                                    )?.name
                                                }
                                            </span>
                                        )}
                                        {appointment.client.cpf && (
                                                <span>
                                                    {documentLabel(
                                                        appointment.client.cpf
                                                    )}{' '}
                                                    {appointment.client.cpf}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* "Definir" abre a lista de controles e é
                                        o que EXECUTA: escolher o controle já
                                        põe a pessoa no painel. A seta ao lado
                                        escolhe a sala daquela linha antes
                                        disso — os dois do topo continuam sendo
                                        só o padrão da fila. */}
                                    <div className="flex shrink-0 items-stretch">
                                        {assigningKey === key ? (
                                            <span className="btn btn-primary btn-sm !rounded-lg">
                                                <span className="loading loading-spinner loading-xs" />
                                            </span>
                                        ) : (
                                            <>
                                                <OptionDropdown
                                                    ariaLabel={`Definir controle para ${appointment.client.name}`}
                                                    className="btn-primary !rounded-l-lg !rounded-r-none"
                                                    variant="action"
                                                    triggerLabel="Definir"
                                                    value=""
                                                    options={activeStatuses.map(
                                                        status => ({
                                                            value: status.id,
                                                            label: `${status.position}. ${status.name}`,
                                                            color: status.color,
                                                        })
                                                    )}
                                                    onChange={value =>
                                                        handleAssign(
                                                            appointment,
                                                            Number(value)
                                                        )
                                                    }
                                                />
                                                <OptionDropdown
                                                    ariaLabel={`Sala para ${appointment.client.name}`}
                                                    className="btn-primary !rounded-l-none !rounded-r-lg !border-l !border-primary-content/25 !px-2"
                                                    variant="action"
                                                    compact
                                                    icon={
                                                        <MdMeetingRoom className="text-base" />
                                                    }
                                                    value={
                                                        roomByAppointment[key] ??
                                                        roomId
                                                    }
                                                    options={[
                                                        {
                                                            value: '',
                                                            label: 'Sem sala definida',
                                                        },
                                                        ...rooms.map(room => ({
                                                            value: room.id,
                                                            label: room.name,
                                                            color: room.color,
                                                            hint: room.description,
                                                        })),
                                                    ]}
                                                    onChange={value =>
                                                        setRoomByAppointment(
                                                            current => ({
                                                                ...current,
                                                                [key]: value,
                                                            })
                                                        )
                                                    }
                                                />
                                            </>
                                        )}
                                    </div>
                                </li>
                            )
                        })}
                    </ul>

                    <PaginationControls
                        page={page}
                        limit={PAGE_LIMIT}
                        total={total}
                        loading={loading}
                        onPageChange={setPage}
                        label="agendamento"
                    />
                </>
            )}
        </div>
    )
}
